// Recent incidents table — resolved/past incidents with details

import { Trash2 } from 'lucide-react'
import { useState } from 'react'
import { StatusBadge } from '@/components/ui/status-badge'
import type { Incident } from '@/lib/types-status'

interface RecentIncidentsTableProps {
  incidents: Incident[]
  onDelete: (id: number) => void
  isDeleting: boolean
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDuration(startIso: string, endIso: string | null): string {
  if (!endIso) return 'Ongoing'
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime()
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ${minutes % 60}m`
  return `${Math.floor(hours / 24)}d ${hours % 24}h`
}

export function RecentIncidentsTable({
  incidents,
  onDelete,
  isDeleting,
}: RecentIncidentsTableProps): React.JSX.Element {
  const [confirmId, setConfirmId] = useState<number | null>(null)

  function handleDelete(id: number): void {
    if (confirmId === id) {
      onDelete(id)
      setConfirmId(null)
    } else {
      setConfirmId(id)
    }
  }

  if (incidents.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-surface-3 p-6 text-center text-sm text-text-tertiary">
        No recent incidents
      </p>
    )
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-surface-3">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-surface-3 bg-surface-1">
            <th className="px-4 py-3 text-left font-medium text-text-secondary">Title</th>
            <th className="px-4 py-3 text-left font-medium text-text-secondary">Severity</th>
            <th className="px-4 py-3 text-left font-medium text-text-secondary">Services</th>
            <th className="px-4 py-3 text-left font-medium text-text-secondary">Started</th>
            <th className="px-4 py-3 text-left font-medium text-text-secondary">Resolved</th>
            <th className="px-4 py-3 text-left font-medium text-text-secondary">Duration</th>
            <th className="px-4 py-3 text-right font-medium text-text-secondary">Actions</th>
          </tr>
        </thead>
        <tbody>
          {incidents.map((incident) => (
            <tr
              key={incident.id}
              className="border-b border-surface-3 last:border-0 hover:bg-surface-1"
            >
              <td className="px-4 py-3 font-medium text-text-primary">{incident.title}</td>
              <td className="px-4 py-3">
                <StatusBadge status={incident.severity} />
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1">
                  {incident.affectedServices.map((svc) => (
                    <span
                      key={svc}
                      className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] font-medium text-text-secondary"
                    >
                      {svc}
                    </span>
                  ))}
                </div>
              </td>
              <td className="px-4 py-3 text-text-secondary">{formatDate(incident.startedAt)}</td>
              <td className="px-4 py-3 text-text-secondary">
                {incident.resolvedAt ? formatDate(incident.resolvedAt) : '—'}
              </td>
              <td className="px-4 py-3 text-text-secondary">
                {formatDuration(incident.startedAt, incident.resolvedAt)}
              </td>
              <td className="px-4 py-3 text-right">
                <button
                  type="button"
                  onClick={() => handleDelete(incident.id)}
                  disabled={isDeleting}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="h-3 w-3" />
                  {confirmId === incident.id ? 'Confirm?' : 'Delete'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
